import type { ReactNode } from "react";
import type { Locale } from "@thyra/landing-i18n";

/**
 * Small inline flags for the language switcher, drawn on a 30x20 canvas.
 * Simplified on purpose: they render at ~20px wide, where emblem detail is
 * invisible. Inline SVG keeps them out of the network waterfall.
 */
const FLAGS: Record<Locale, ReactNode> = {
  en: (
    <>
      <rect width="30" height="20" fill="#fff" />
      {[0, 2, 4, 6, 8, 10, 12].map((stripe) => (
        <rect
          key={stripe}
          y={(stripe * 20) / 13}
          width="30"
          height={20 / 13}
          fill="#B22234"
        />
      ))}
      <rect width="13" height={(20 / 13) * 7} fill="#3C3B6E" />
      {[2.5, 6.5, 10.5].flatMap((x) =>
        [2.4, 5.4, 8.4].map((y) => (
          <circle key={`${x}-${y}`} cx={x} cy={y} r="0.7" fill="#fff" />
        )),
      )}
    </>
  ),
  "pt-BR": (
    <>
      <rect width="30" height="20" fill="#009C3B" />
      <path d="M15 2.5 27 10 15 17.5 3 10Z" fill="#FFDF00" />
      <circle cx="15" cy="10" r="4.3" fill="#002776" />
      <path
        d="M10.9 9.1c3-.6 6 .2 8.2 2.1"
        fill="none"
        stroke="#fff"
        strokeWidth="0.9"
      />
    </>
  ),
  "pt-PT": (
    <>
      <rect width="30" height="20" fill="#FF0000" />
      <rect width="12" height="20" fill="#006600" />
      <circle cx="12" cy="10" r="4" fill="#FFE900" />
      <path d="M10 7.6h4v3a2 2 0 0 1-4 0Z" fill="#fff" stroke="#FF0000" />
    </>
  ),
  fr: (
    <>
      <rect width="30" height="20" fill="#fff" />
      <rect width="10" height="20" fill="#0055A4" />
      <rect x="20" width="10" height="20" fill="#EF4135" />
    </>
  ),
  de: (
    <>
      <rect width="30" height="20" fill="#DD0000" />
      <rect width="30" height="6.67" fill="#000" />
      <rect y="13.33" width="30" height="6.67" fill="#FFCE00" />
    </>
  ),
  es: (
    <>
      <rect width="30" height="20" fill="#AA151B" />
      <rect y="5" width="30" height="10" fill="#F1BF00" />
    </>
  ),
  it: (
    <>
      <rect width="30" height="20" fill="#fff" />
      <rect width="10" height="20" fill="#009246" />
      <rect x="20" width="10" height="20" fill="#CE2B37" />
    </>
  ),
};

type FlagIconProps = {
  locale: Locale;
  className?: string;
};

/** Decorative: the language name next to it carries the meaning. */
export function FlagIcon({ locale, className = "" }: FlagIconProps) {
  return (
    <svg
      viewBox="0 0 30 20"
      aria-hidden
      className={`h-3.5 w-[21px] shrink-0 rounded-[3px] ring-1 ring-black/10 dark:ring-white/15 ${className}`}
    >
      {FLAGS[locale]}
    </svg>
  );
}
